import { cn } from "@/utilities/app-utils";
import { LoadingSpinner } from "./loading-spinner";
import { ModeToggle } from "./mode-toggle";
import { useMemo } from "react";

interface LoadingCardProps {
  /**
   * The loading message to display
   */
  text?: string;

  /**
   * Size of the loading spinner
   */
  spinnerSize?: "sm" | "md" | "lg" | "xl";

  /**
   * Whether to show the theme toggle in the top-right corner
   */
  showThemeToggle?: boolean;

  /**
   * Custom className for the card container
   */
  className?: string;

  /**
   * Whether to use full-screen overlay (default: true)
   */
  fullScreen?: boolean;

  /**
   * Custom className for the card itself
   */
  cardClassName?: string;
}

/**
 * Unified LoadingCard component for consistent loading states across login and registration
 *
 * Features:
 * - Consistent card size (max-w-md)
 * - Consistent border and styling
 * - Consistent placement (centered with fixed overlay)
 * - Consistent loading indicator (LoadingSpinner)
 * - Optional theme toggle
 * - Configurable spinner size and text
 */
export function LoadingCard({
  text,
  spinnerSize = "lg",
  showThemeToggle = true,
  className,
  fullScreen = true,
  cardClassName
}: LoadingCardProps) {
  const containerClasses = useMemo(
    () =>
      fullScreen
        ? "fixed inset-0 bg-background flex items-center justify-center z-50"
        : "flex items-center justify-center",
    [fullScreen]
  );

  return (
    <div className={cn(containerClasses, className)}>
      {/* Theme toggle in top-right corner */}
      {showThemeToggle && fullScreen && (
        <div className="absolute top-4 right-4 z-10">
          <ModeToggle />
        </div>
      )}

      {/* Loading Card */}
      <div className={cn("w-full max-w-md", cardClassName)}>
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size={spinnerSize} text={text} />
        </div>
      </div>
    </div>
  );
}
